import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../../../../api/auth/useAuth";
import { useCompanyProfile } from "../../../../../../../../api/services/financial-integration.hooks";
import { ArrowUpRightIcon } from "../../../../../../../../assets/icons";
import { LockDisplay } from "../../../../../../../components/LockDisplay/LockDisplay";
import styles from "./CompanyProfile.module.css";

// Интерфейс для деталей компании
interface CompanyDetail {
  label: string;
  value: string;
  isLink?: boolean;
}

interface CompanyProfileProps {
  tabAccessToken?: string;
}

export const CompanyProfile = ({
  tabAccessToken,
}: CompanyProfileProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { symbol } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: profileResponse, isLoading } = useCompanyProfile({
    symbol: symbol || "",
    tabAccessToken,
  });

  const profileData = profileResponse?.data;

  // Формируем адрес из отдельных полей
  const formatAddress = (): string => {
    if (!profileData) return "";
    const parts = [
      profileData.address,
      profileData.city,
      profileData.state,
      profileData.zip,
      profileData.country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Формируем детали компании из данных API
  const companyDetails: CompanyDetail[] = profileData
    ? [
        {
          label: "Sector",
          value: profileData.sector || "N/A",
          // isLink: true,
        },
        {
          label: "Employees",
          value: profileData.employees || "N/A",
        },
        {
          label: "Industry",
          value: profileData.industry || "N/A",
          // isLink: true,
        },
        {
          label: "Founded",
          value: profileData.founded || "N/A",
        },
        {
          label: "Website",
          value: profileData.website || "N/A",
          isLink: !!profileData.website,
        },
        {
          label: "Phone Number",
          value: profileData.phone || "N/A",
        },
        {
          label: "Address",
          value: formatAddress() || "N/A",
        },
      ]
    : [];

  const handleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t("pages.topHalal.info.companyProfile.title")}
          </h3>
        </div>
        <LockDisplay height="220px" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Company Profile</h3>
        </div>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Company Profile</h3>
        </div>
        <div className={styles.empty}>No data available</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>Company Profile</h3>
          {profileData.image && (
            <img
              src={profileData.image}
              alt={`${symbol} Logo`}
              className={styles.logo}
            />
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.aboutSection}>
            <p
              className={styles.aboutText}
              style={{
                WebkitLineClamp: isExpanded ? "unset" : 3,
                display: isExpanded ? "block" : "-webkit-box",
              }}
            >
              {profileData.description || "No description available"}
            </p>
            {profileData.description && (
              <button
                className={styles.readMoreButton}
                onClick={handleReadMore}
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </div>

          <div className={styles.detailsSection}>
            {companyDetails.map((detail, index) => (
              <div key={index} className={styles.detailItem}>
                <div className={styles.detailLabel}>{detail.label}</div>
                {detail.isLink ? (
                  detail.isLink ? (
                    <a
                      href={
                        detail.label === "Website" && profileData.website
                          ? `https://${profileData.website.replace(
                              /^https?:\/\//,
                              ""
                            )}`
                          : "#"
                      }
                      className={styles.detailLink}
                      target={detail.label === "Website" ? "_blank" : undefined}
                      rel={
                        detail.label === "Website"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      onClick={(e) => {
                        if (detail.label !== "Website") {
                          e.preventDefault();
                        }
                      }}
                    >
                      {detail.value}
                      <ArrowUpRightIcon />
                    </a>
                  ) : (
                    <div className={styles.detail}>{detail.value}</div>
                  )
                ) : detail.label === "Address" ? (
                  <div className={styles.addressValue}>{detail.value}</div>
                ) : (
                  <div className={styles.detailValue}>{detail.value}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
